import { useController } from "react-hook-form";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { InputError } from "./input-error";
import { cn } from "@/lib/utils";
import { KButton } from "@/components/k-button";
import type { ReactNode } from "react";
import { Add, Remove } from "@mui/icons-material";
import { z } from "zod";
import { ZodControlledField } from "@/common/use-k-form";

type KInputProps<TFieldValues extends z.Schema> = {
    label?: string;
    disabled?: boolean;
    type?: "text" | "number" | "password" | "hidden";
    className?: string;
    inputClassName?: string;
    prefix?: ReactNode;
    readOnly?: boolean;
    placeholder?: string;
    step?: number;
    min?: number;
    hideStepButtons?: boolean;
} & ZodControlledField<TFieldValues>;

export function KInput<TFieldValues extends z.Schema>(
    props: KInputProps<TFieldValues>,
) {
    const { field, fieldState } = useController({
        name: props.name,
        control: props.control,
    });

    const inputType = props.type ?? "text";
    const inputProps = props.control.register(props.name, {
        valueAsNumber: inputType === "number",
    });

    const className = props.className ?? "";

    const inputClassName = cn(
        props.type === "number" && "text-right",
        props.inputClassName,
        props.prefix && "pl-0",
    );

    const min = props.min ?? (inputProps.min as number | undefined);

    const sumToField = (value: number) => {
        const oldValue = parseFloat(field.value);
        const newValue = (isNaN(oldValue) ? 0 : oldValue) + value;
        if (min !== undefined && newValue < min) return;
        field.onChange(newValue);
    };

    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            {!!props.label && (
                <Label
                    htmlFor={field.name}
                    className={
                        fieldState.error ? "text-red-500 dark:text-red-900" : ""
                    }
                >
                    {props.label}
                </Label>
            )}

            <div className="relative">
                <div className="flex rounded-sm border border-neutral-300">
                    {props.prefix && (
                        <div className="flex h-10 w-10 items-center justify-center">
                            {props.prefix}
                        </div>
                    )}
                    <Input
                        {...inputProps}
                        value={field.value}
                        type={inputType}
                        className={cn(
                            "hide-step-buttons h-10 flex-1 border-none font-extralight focus-visible:ring-0 focus-visible:ring-offset-0",
                            inputClassName,
                        )}
                        min={min}
                        disabled={props.disabled || inputProps.disabled}
                        readOnly={props.readOnly}
                        placeholder={props.placeholder}
                    />
                    {inputType === "number" && !props.hideStepButtons ? (
                        <div className="flex aspect-square flex-col items-center border-l border-neutral-300">
                            <KButton
                                className="m-0 h-min w-full flex-1 rounded-none p-0"
                                variant="ghost"
                                onClick={() => sumToField(props.step ?? 1)}
                            >
                                <Add className="size-4" />
                            </KButton>
                            <div className="w-full border-b border-neutral-300" />
                            <KButton
                                className="m-0 h-min w-full flex-1 rounded-none p-0"
                                variant="ghost"
                                onClick={() => sumToField(-(props.step ?? 1))}
                            >
                                <Remove />
                            </KButton>
                        </div>
                    ) : null}
                </div>
                {inputType !== "hidden" && !!fieldState.error?.message && (
                    <InputError errorMessage={fieldState.error.message} />
                )}
            </div>
        </div>
    );
}
